import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Hls from 'hls.js';
import {
  api, streamHlsProxyUrl,
  getCameras, createCamera, deleteCamera,
  startCameraAI, stopCameraAI, getActiveStreams,
  type Camera,
} from '../api/client';
import { useAuth } from '../AuthContext';
import PredictiveAnalytics from '../components/PredictiveAnalytics';
import TableManagementModal from '../components/TableManagementModal';

const GlobalStyle = createGlobalStyle`
  * { box-sizing: border-box; }
  button { font-family: inherit; }
  input, textarea, select { font-family: inherit; }
`;

// Styled Components
const DashboardContainer = styled.div`
  min-height: 100vh;
  background-color: var(--color-neutral-100, #F5F4F1);
  padding: 24px;
`;

const AttrHeader = styled.header`
  background: var(--color-primary-900, #1A1D21);
  border-bottom: 1px solid var(--color-primary-800, #2B3035);
  padding: 0 24px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: -24px -24px 32px -24px;
  position: sticky;
  top: 0;
  z-index: 100;
`;

const AttrBrand = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  text-decoration: none;
`;

const AttrBrandLogo = styled.img`
  width: 32px;
  height: 32px;
  object-fit: contain;
`;

const AttrBrandText = styled.div`
  display: flex;
  flex-direction: column;
`;

const AttrWordmark = styled.span`
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
  letter-spacing: -0.01em;
  line-height: 1.1;
`;

const AttrProductLine = styled.span`
  font-size: 10px;
  font-weight: 600;
  color: var(--color-secondary-400, #7FC6A8);
  text-transform: uppercase;
  letter-spacing: 0.1em;
  line-height: 1.1;
`;

const AttrHeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
`;

const AttrUserInfo = styled.span`
  font-size: 13px;
  color: var(--color-primary-400, #ADB5BD);
  font-weight: 500;
`;


const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'success' | 'outline' }>`
  padding: 8px 18px;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 14px;
  font-family: inherit;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-height: 38px;
  
  ${props => props.variant === 'primary' && `
    background: var(--color-primary-800, #2B3035);
    color: white;
    &:hover {
      background: var(--color-primary-900, #1A1D21);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(26,29,33,0.3);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: white;
    color: var(--color-primary-800, #2B3035);
    border: 1px solid var(--color-neutral-300, #D3D0C9);
    &:hover {
      background: var(--color-neutral-50, #FAFAF9);
      border-color: var(--color-neutral-400, #B8B4AB);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }
  `}
  
  ${props => props.variant === 'success' && `
    background: var(--color-secondary-600, #4A8A6F);
    color: white;
    &:hover {
      background: var(--color-secondary-700, #396C56);
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(74,138,111,0.3);
    }
  `}

  ${props => props.variant === 'outline' && `
    background: transparent;
    color: var(--color-primary-700, #343A40);
    border: 1.5px solid var(--color-neutral-300, #D3D0C9);
    &:hover {
      border-color: var(--color-primary-600, #495057);
      background: var(--color-neutral-100, #F5F4F1);
    }
  `}
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const Card = styled.div<{ variant?: string }>`
  background: #ffffff;
  border-radius: 12px;
  padding: 28px;
  box-shadow: var(--shadow-sm, 0 1px 2px 0 rgba(0,0,0,0.05));
  border: 1px solid var(--color-neutral-200, #E8E6E1);
  transition: all 0.25s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: var(--shadow-lg, 0 10px 15px -3px rgba(0,0,0,0.1));
    border-color: var(--color-neutral-300, #D3D0C9);
  }
  
  ${props => props.variant === 'gradient' && `
    background: linear-gradient(135deg, var(--color-primary-900, #1A1D21) 0%, var(--color-primary-700, #343A40) 100%);
    color: white;
    border: none;
    box-shadow: 0 10px 25px rgba(26,29,33,0.35);
  `}
  
  ${props => props.variant === 'alert' && `
    background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
    color: white;
    border: none;
    box-shadow: 0 10px 25px rgba(220, 38, 38, 0.3);
  `}
  
  ${props => props.variant === 'success' && `
    background: linear-gradient(135deg, var(--color-secondary-600, #4A8A6F) 0%, var(--color-secondary-800, #2A4E3E) 100%);
    color: white;
    border: none;
    box-shadow: 0 10px 25px rgba(74,138,111,0.3);
  `}
`;

const CardTitle = styled.h3<{ $light?: boolean }>`
  font-size: 11px;
  font-weight: 600;
  margin: 0 0 20px 0;
  color: ${props => props.$light ? 'rgba(255,255,255,0.85)' : 'var(--color-primary-500, #6C757D)'};
  text-transform: uppercase;
  letter-spacing: 0.07em;
`;

const CardValue = styled.div<{ $light?: boolean }>`
  font-size: 46px;
  font-weight: 800;
  margin: 0 0 8px 0;
  color: ${props => props.$light ? '#ffffff' : 'var(--color-primary-900, #1A1D21)'};
  display: flex;
  align-items: center;
  gap: 12px;
  line-height: 1;
`;

const CardSubtext = styled.div<{ $light?: boolean }>`
  font-size: 13px;
  color: ${props => props.$light ? 'rgba(255,255,255,0.8)' : 'var(--color-primary-500, #6C757D)'};
  margin-top: 8px;
  font-weight: 500;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  margin-bottom: 32px;
`;

const StatCard = styled(Card)`
  text-align: center;
`;

const ActivitySection = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
`;

const TableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 20px;
  margin-top: 24px;
`;

const TableCard = styled.div<{ status: string }>`
  background: ${props => {
    switch(props.status) {
      case 'occupied': return 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
      case 'needs_clear': return 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)';
      case 'reserved': return 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)';
      default: return 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)';
    }
  }};
  color: white;
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;
  cursor: pointer;
  border: none;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.1);
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1);

    &::before {
      opacity: 1;
    }
  }
`;

const TableName = styled.div`
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.2;
`;

const TableStatus = styled.div`
  font-size: 12px;
  opacity: 0.9;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-weight: 600;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: var(--color-neutral-50, #FAFAF9);
  border-radius: 10px;
  border: 1px solid var(--color-neutral-200, #E8E6E1);
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-neutral-100, #F5F4F1);
    border-color: var(--color-neutral-300, #D3D0C9);
  }
`;

const ActivityIcon = styled.div<{ type: string }>`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;

  ${props => {
    switch(props.type) {
      case 'eating': return 'background: linear-gradient(135deg, #fef3c7 0%, #fbbf24 100%);';
      case 'waiting': return 'background: linear-gradient(135deg, #dbeafe 0%, #3b82f6 100%);';
      case 'phone': return 'background: linear-gradient(135deg, #e0e7ff 0%, #6366f1 100%);';
      case 'leaving': return 'background: linear-gradient(135deg, #fee2e2 0%, #ef4444 100%);';
      default: return 'background: linear-gradient(135deg, #f3f4f6 0%, #6b7280 100%);';
    }
  }}
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-weight: 600;
  color: var(--color-primary-900, #1A1D21);
  font-size: 14px;
  margin-bottom: 2px;
`;

const ActivityDescription = styled.div`
  font-size: 13px;
  color: var(--color-primary-500, #6C757D);
`;

const ActivityTime = styled.div`
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
`;

const VideoContainer = styled.div`
  background: #1e293b;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(30, 41, 59, 0.3);
  border: 1px solid #334155;
`;


const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.85);
  backdrop-filter: blur(10px);
  z-index: 1000;
  align-items: center;
  justify-content: center;
  padding: 20px;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 1400px;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  padding: 32px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #f44336;
  color: white;
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  font-size: 20px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  &:hover {
    background: #d32f2f;
    transform: rotate(90deg);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
  }
`;



const ConnectionStatus = styled.div<{ connected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: ${props => props.connected ? 'rgba(95,168,136,0.12)' : 'rgba(239,68,68,0.1)'};
  border-radius: 8px;
  color: ${props => props.connected ? 'var(--color-secondary-600, #4A8A6F)' : 'var(--color-error, #EF4444)'};
  font-weight: 600;
  font-size: 13px;
`;

// ── Camera management styled components ───────────────────────────────────────

const CameraGrid = styled.div<{ $count: number }>`
  display: grid;
  grid-template-columns: ${props =>
    props.$count === 1 ? '1fr' :
    props.$count <= 4 ? 'repeat(2, 1fr)' :
    'repeat(3, 1fr)'};
  gap: 16px;
  margin-top: 16px;
`;

const CameraCell = styled.div<{ $selected?: boolean }>`
  position: relative;
  background: #0f172a;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  border: 2px solid ${props => props.$selected ? 'var(--color-secondary-400, #7FC6A8)' : 'transparent'};
  transition: border-color 0.2s;
  &:hover { border-color: rgba(127,198,168,0.5); }
`;

const CameraLabel = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(0,0,0,0.75);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 6px;
  z-index: 2;
  backdrop-filter: blur(4px);
`;

const AiStatusBadge = styled.span<{ $live: boolean }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$live ? '#22c55e' : '#ef4444'};
  margin-right: 5px;
`;

const CameraControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(transparent, rgba(0,0,0,0.85));
  padding: 28px 12px 10px;
  display: flex;
  gap: 8px;
  z-index: 2;
`;

const SmallButton = styled.button<{ $danger?: boolean; $success?: boolean }>`
  flex: 1;
  padding: 5px 8px;
  border-radius: 6px;
  border: none;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.$danger ? '#dc2626' : props.$success ? '#16a34a' : 'rgba(255,255,255,0.15)'};
  color: white;
  backdrop-filter: blur(4px);
  &:hover { opacity: 0.85; transform: translateY(-1px); }
  &:disabled { opacity: 0.4; cursor: not-allowed; transform: none; }
`;

const CameraFormRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: flex-end;
  flex-wrap: wrap;
  margin-bottom: 16px;
  padding: 16px;
  background: var(--color-neutral-50, #FAFAF9);
  border-radius: 10px;
  border: 1px solid var(--color-neutral-200, #E8E6E1);
`;

const FormField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 140px;
`;

const FormLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  color: var(--color-primary-500, #6C757D);
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const FormInput = styled.input`
  padding: 8px 12px;
  border: 1px solid var(--color-neutral-300, #D3D0C9);
  border-radius: 6px;
  font-size: 13px;
  font-family: inherit;
  outline: none;
  background: white;
  &:focus { border-color: var(--color-secondary-400, #7FC6A8); box-shadow: 0 0 0 3px rgba(127,198,168,0.15); }
`;

const CameraListRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: white;
  border-radius: 8px;
  border: 1px solid var(--color-neutral-200, #E8E6E1);
  margin-bottom: 8px;
`;

const EmptyCameraHint = styled.div`
  text-align: center;
  padding: 48px 20px;
  color: var(--color-primary-500, #6C757D);
  background: var(--color-neutral-50, #FAFAF9);
  border-radius: 12px;
  border: 2px dashed var(--color-neutral-300, #D3D0C9);
  font-size: 14px;
  line-height: 1.7;
`;

// ─────────────────────────────────────────────────────────────────────────────

type TableItem = { id: number; name: string; status: 'free'|'occupied'|'needs_clear'|'reserved' };
type OverlayData = {
  width: number;
  height: number;
  detections: Array<{ bbox: number[]; score: number; class: string }>;
  peopleCount: number;
  trackedObjects?: Array<{ id: number; bbox: number[]; class: string; activity: string; age: number }>;
  zones?: Record<string, any>;
  tableStates?: Record<string, any>;
  foodSummary?: { food_items: number; plates_and_cups: number; items: any[] };
  modelInfo?: { model: string; confidence_threshold: number };
};

export default function DashboardModern() {
  const { token, user, logout, selectedBusiness } = useAuth();
  const [tables, setTables] = useState<TableItem[]>([]);
  const [turnover, setTurnover] = useState<number>(0);
  const [needsClear, setNeedsClear] = useState<number>(0);

  // ── Camera management state ────────────────────────────────────────────────
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [aiActiveUrls, setAiActiveUrls] = useState<Set<string>>(new Set());
  const [selectedCameraId, setSelectedCameraId] = useState<number | null>(null);
  const [cameraModalOpen, setCameraModalOpen] = useState(false);
  const [newCamName, setNewCamName] = useState('');
  const [newCamUrl, setNewCamUrl] = useState('');
  const [addingCamera, setAddingCamera] = useState(false);
  const [aiLoadingId, setAiLoadingId] = useState<number | null>(null);
  const defaultLocationId = useRef<number>(1);
  // ──────────────────────────────────────────────────────────────────────────

  const wsUrl = useMemo(
    () => {
      const base = (process.env.REACT_APP_WS_BASE || 'ws://localhost:4000') + '/ws';
      return token ? `${base}?token=${encodeURIComponent(token)}` : base;
    },
    [token],
  );
  const [overlay, setOverlay] = useState<OverlayData | null>(null);
  const [insights, setInsights] = useState<Array<string>>([]);
  const [wsConnected, setWsConnected] = useState(false);
  const [tableManagementOpen, setTableManagementOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState<any>(null);
  const [confirmedTables, setConfirmedTables] = useState<any[]>([]);
  const [confirmedTableMap, setConfirmedTableMap] = useState<Record<number, any>>({});
  const [dbTables, setDbTables] = useState<any[]>([]);
  const playerRef = useRef<HTMLDivElement>(null);
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const shouldReconnectRef = useRef(true);

  const selectedCamera = cameras.find(c => c.id === selectedCameraId) ?? cameras[0] ?? null;

  // Load confirmed tables from database
  useEffect(() => {
    const loadConfirmedTables = async () => {
      try {
        const response = await api.get('/table-management/confirmed?locationId=1');
        setConfirmedTables(response.data.tables || response.data);
        setConfirmedTableMap(response.data.tableMap || {});
        console.log('[TableManagement] Loaded confirmed tables:', response.data.tableMap);
      } catch (error) {
        console.error('Failed to load confirmed tables:', error);
      }
    };
    loadConfirmedTables();
    
    // Reload every 30 seconds to catch updates
    const interval = setInterval(loadConfirmedTables, 30000);
    return () => clearInterval(interval);
  }, []);

  // Load AI-synced DB table statuses so overlay matches the dashboard list
  useEffect(() => {
    const loadDbTables = async () => {
      try {
        const response = await api.get('/ai-tables/status?locationId=1');
        setDbTables(response.data.tables || []);
      } catch (e) {
        // silent
      }
    };
    loadDbTables();
    const interval = setInterval(loadDbTables, 15000);
    return () => clearInterval(interval);
  }, []);

  // ── Load cameras + active AI streams ─────────────────────────────────────
  useEffect(() => {
    if (!token) return;
    const load = async () => {
      try {
        const cams = await getCameras();
        setCameras(cams);
        if (cams.length > 0) {
          setSelectedCameraId(prev => prev ?? cams[0]!.id);
          defaultLocationId.current = cams[0]!.locationId;
        } else {
          // Ensure a default location exists for new cameras
          try {
            const locRes = await api.get('/locations');
            const locs = locRes.data as { id: number }[];
            if (locs.length > 0) defaultLocationId.current = locs[0]!.id;
            else {
              const newLoc = await api.post('/locations', { name: 'Main Restaurant' });
              defaultLocationId.current = newLoc.data.id;
            }
          } catch { /* ignore */ }
        }
        const { activeStreams: active } = await getActiveStreams();
        setAiActiveUrls(new Set(active));
      } catch (e) {
        console.error('Failed to load cameras:', e);
      }
    };
    load();
  }, [token]);

  const handleAddCamera = useCallback(async () => {
    if (!newCamName.trim() || !newCamUrl.trim()) return;
    setAddingCamera(true);
    try {
      const cam = await createCamera(defaultLocationId.current, newCamName.trim(), newCamUrl.trim());
      setCameras(prev => [...prev, cam]);
      setSelectedCameraId(id => id ?? cam.id);
      setNewCamName('');
      setNewCamUrl('');
    } catch (e) {
      console.error('Failed to add camera:', e);
      alert('Failed to add camera. Check the URL and try again.');
    } finally {
      setAddingCamera(false);
    }
  }, [newCamName, newCamUrl]);

  const handleDeleteCamera = useCallback(async (camId: number) => {
    if (!window.confirm('Delete this camera?')) return;
    try {
      await deleteCamera(camId);
      setCameras(prev => {
        const next = prev.filter(c => c.id !== camId);
        setSelectedCameraId(id => id === camId ? (next[0]?.id ?? null) : id);
        return next;
      });
    } catch (e) {
      console.error('Failed to delete camera:', e);
    }
  }, []);

  const handleToggleAI = useCallback(async (cam: Camera) => {
    setAiLoadingId(cam.id);
    try {
      if (aiActiveUrls.has(cam.streamUrl)) {
        await stopCameraAI(cam.id);
        setAiActiveUrls(prev => { const n = new Set(prev); n.delete(cam.streamUrl); return n; });
      } else {
        await startCameraAI(cam.id);
        setAiActiveUrls(prev => { const n = new Set(prev); n.add(cam.streamUrl); return n; });
      }
    } catch (e) {
      console.error('Failed to toggle AI:', e);
    } finally {
      setAiLoadingId(null);
    }
  }, [aiActiveUrls]);
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!token) return;
    
    // Use AI-synced tables instead of old /api/tables endpoint
    const loadTables = async () => {
      try {
        const response = await api.get('/ai-tables/status?locationId=1');
        const aiTables = response.data.tables || [];
        // Convert to old format for compatibility
        const formattedTables = aiTables.map((t: any) => ({
          id: t.id,
          name: t.name,
          status: t.status as 'free'|'occupied'|'needs_clear'|'reserved'
        }));
        setTables(formattedTables);
      } catch (error) {
        console.error('Failed to load AI-synced tables:', error);
        // Fallback to old endpoint
    api.get('/tables').then(r => setTables(r.data));
      }
    };
    
    loadTables();
    Promise.all([
      api.get('/metrics/turnover').then(r => setTurnover(r.data.last24hFreed)),
      api.get('/metrics/needs-clear').then(r => setNeedsClear(r.data.needsClearCount)),
    ]);
    
    shouldReconnectRef.current = true;
    
    const connectWS = () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('[UI] WebSocket connected');
        setWsConnected(true);
      };
      ws.onclose = () => {
        console.log('[UI] WebSocket disconnected');
        setWsConnected(false);
        
        if (shouldReconnectRef.current) {
          reconnectTimeoutRef.current = setTimeout(() => {
            if (shouldReconnectRef.current) {
              connectWS();
            }
          }, 3000);
        }
      };
      ws.onerror = (error) => {
        console.error('[UI] WebSocket error:', error);
        setWsConnected(false);
      };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'table.status') {
            setTables(prev => prev.map(t => t.id === msg.payload.tableId ? { ...t, status: msg.payload.status } : t));
          } else if (msg.type === 'ai.status') {
            const { streamUrl, status, resolved } = msg.payload || {};
            if (status === 'stopped' || status === 'ended' || status === 'error') {
              setAiActiveUrls(prev => { const n = new Set(prev); n.delete(streamUrl); return n; });
            } else if (streamUrl) {
              setAiActiveUrls(prev => { const n = new Set(prev); n.add(streamUrl); return n; });
            }
            if (resolved != null) {
              setInsights(prev => [`AI stream resolved: ${resolved ? 'yes' : 'no'}`, ...prev].slice(0,5));
            }
          } else if (msg.type === 'ai.detections') {
            const { width, height, detections, peopleCount, trackedObjects, zones, tableStates, foodSummary, modelInfo } = msg.payload;
            setOverlay({
              width,
              height,
              detections: (detections || []).map((d: any) => ({
                bbox: d.bbox,
                score: d.score,
                class: d.class
              })),
              peopleCount,
              trackedObjects: trackedObjects || [],
              zones: zones || {},
              tableStates: tableStates || {},
              foodSummary: foodSummary || { food_items: 0, plates_and_cups: 0, items: [] },
              modelInfo: modelInfo || {}
            });
          } else if (msg.type === 'ai.insight') {
            const { durationMs } = msg.payload || {};
            if (durationMs != null) {
              const mins = Math.round((durationMs/1000)/60);
              setInsights(prev => [`Session length: ${mins} min`, ...prev].slice(0, 5));
            }
          }
        } catch (e) {
          console.error('[UI] Error parsing WebSocket message:', e);
        }
      };
    };
    
    connectWS();
    
    return () => {
      shouldReconnectRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [wsUrl, token]);

  // Calculate analytics
  // Use AI-detected tables if available, otherwise use database tables
  const aiTables = overlay?.tableStates ? Object.values(overlay.tableStates) : [];
  const totalTables = aiTables.length > 0 ? aiTables.length : tables.length;
  const occupiedTables = aiTables.length > 0 
    ? aiTables.filter((t: any) => t.state === 'occupied').length 
    : tables.filter(t => t.status === 'occupied').length;
  const tablesNeedingClear = aiTables.length > 0
    ? aiTables.filter((t: any) => t.state === 'needs_clear').length
    : needsClear;
  const occupancyRate = totalTables > 0 ? Math.round((occupiedTables / totalTables) * 100) : 0;
  const activePeople = overlay?.trackedObjects?.length || 0;
  const activityCounts = overlay?.trackedObjects?.reduce((acc, t) => {
    acc[t.activity] = (acc[t.activity] || 0) + 1;
    return acc;
  }, {} as Record<string, number>) || {};


  return (
    <>
      <GlobalStyle />
    <DashboardContainer>
      {/* Attractis branded header */}
      <AttrHeader>
        <AttrBrand onClick={() => {}}>
          <AttrBrandLogo src={`${process.env.PUBLIC_URL}/arch-logo.png`} alt="Attractis" />
          <AttrBrandText>
            <AttrWordmark>Attractis{selectedBusiness ? ` — ${selectedBusiness.name}` : ''}</AttrWordmark>
            <AttrProductLine>Table Vision</AttrProductLine>
          </AttrBrandText>
        </AttrBrand>
        <AttrHeaderActions>
          <ConnectionStatus connected={wsConnected}>
            <span style={{ fontSize: 8 }}>{wsConnected ? '●' : '●'}</span>
            {wsConnected ? 'Live' : 'Offline'}
          </ConnectionStatus>
          {user?.email && <AttrUserInfo>{user.email}</AttrUserInfo>}
          <Button variant="outline" onClick={() => setCameraModalOpen(true)}>
            📹 Cameras{cameras.length > 0 ? ` (${cameras.length})` : ''}
          </Button>
          <Button variant="secondary" onClick={logout} style={{ minHeight: 36, padding: '6px 14px' }}>
            Logout
          </Button>
        </AttrHeaderActions>
      </AttrHeader>

      {/* Key Metrics */}
      <StatsGrid>
        <StatCard variant="gradient">
          <CardTitle $light>Current Occupancy</CardTitle>
          <CardValue $light>{occupancyRate}%</CardValue>
          <CardSubtext $light>{occupiedTables} of {totalTables} tables {aiTables.length > 0 ? '(AI)' : '(DB)'}</CardSubtext>
        </StatCard>
        
        <StatCard variant="success">
          <CardTitle $light>Active Customers</CardTitle>
          <CardValue $light>{overlay?.peopleCount || 0}</CardValue>
          <CardSubtext $light>Tracked: {activePeople} individuals</CardSubtext>
        </StatCard>
        
        <StatCard>
          <CardTitle>Turnover (24h)</CardTitle>
          <CardValue>{turnover}</CardValue>
          <CardSubtext>Tables served today</CardSubtext>
        </StatCard>
        
        <StatCard variant={tablesNeedingClear > 0 ? 'alert' : undefined}>
          <CardTitle $light={tablesNeedingClear > 0}>Needs Clearing</CardTitle>
          <CardValue $light={tablesNeedingClear > 0}>{tablesNeedingClear}</CardValue>
          <CardSubtext $light={tablesNeedingClear > 0}>{tablesNeedingClear > 0 ? 'Action required!' : 'All clear'}</CardSubtext>
        </StatCard>
      </StatsGrid>

      {/* Activity & Tables */}
      <ActivitySection>
        <Card>
          <CardTitle>Table Status Overview</CardTitle>
          <TableGrid>
            {/* Show AI-detected tables if available, otherwise show database tables */}
            {overlay?.tableStates && Object.keys(overlay.tableStates).length > 0 ? (
              Object.entries(overlay.tableStates).map(([key, table]: [string, any]) => {
                // Extract table number from key (e.g., "table_5" -> 5)
                const tableNum = parseInt(key.replace('table_', ''));
                // Get confirmed custom name from confirmedTableMap or dbTables
                const confirmedInfo = confirmedTableMap[tableNum];
                const dbTableInfo = dbTables.find(t => t.name.includes(`${tableNum}`));
                const displayName = confirmedInfo?.displayName || dbTableInfo?.name || key.replace('_', ' ').toUpperCase();
                
                return (
                <TableCard key={key} status={table.state}>
                    <TableName>{displayName}</TableName>
                  <TableStatus>{table.state.replace('_', ' ')}</TableStatus>
                  <div style={{ fontSize: '11px', marginTop: '4px', opacity: 0.8 }}>
                    Confidence: {Math.round(table.confidence * 100)}%
                  </div>
                </TableCard>
                );
              })
            ) : tables.length > 0 ? (
              tables.map(table => (
                <TableCard key={table.id} status={table.status}>
                  <TableName>{table.name}</TableName>
                  <TableStatus>{table.status.replace('_', ' ')}</TableStatus>
                </TableCard>
              ))
            ) : (
              <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '40px', opacity: 0.6 }}>
                No tables detected yet. Start AI to detect tables from camera feed.
              </div>
            )}
          </TableGrid>
        </Card>

        <Card>
          <CardTitle>Customer Activities</CardTitle>
          
          {/* Activity Overview Summary */}
          <ActivityList>
            {Object.entries(activityCounts).length > 0 ? (
              Object.entries(activityCounts).map(([activity, count]) => (
                <ActivityItem key={activity}>
                  <ActivityIcon type={activity}>
                      {activity === 'eating' ? '🍽️' : 
                       activity === 'waiting' ? '⏳' : 
                     activity === 'drinking' ? '☕' :
                     activity === 'leaving' ? '🚶' :
                     activity === 'preparing_to_eat' ? '🍴' :
                     activity === 'using_phone' ? '📱' : '💺'}
                  </ActivityIcon>
                  <ActivityContent>
                    <ActivityTitle>{activity.replace('_', ' ')}</ActivityTitle>
                    <ActivityDescription>{count} customers detected</ActivityDescription>
                  </ActivityContent>
                  <ActivityTime>×{count}</ActivityTime>
                </ActivityItem>
              ))
            ) : (
              <ActivityItem>
                <div style={{ opacity: 0.6, textAlign: 'center', width: '100%' }}>
                  No activity detected yet
                </div>
              </ActivityItem>
            )}
          </ActivityList>

          {/* Individual User Details */}
          {overlay?.trackedObjects && overlay.trackedObjects.length > 0 && (
            <div style={{ marginTop: 20, paddingTop: 20, borderTop: '2px solid #f0f0f0' }}>
              <div style={{ 
                fontSize: 12, 
                fontWeight: 600, 
                marginBottom: 12, 
                opacity: 0.7, 
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}>
                👥 Individual Users ({overlay.trackedObjects.length})
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {overlay.trackedObjects.map((track) => {
                  const activityEmoji = {
                    'eating': '🍽️',
                    'drinking': '☕',
                    'waiting': '⏳',
                    'seated': '💺',
                    'leaving': '🚶',
                    'preparing_to_eat': '🍴',
                    'using_phone': '📱'
                  }[track.activity] || '👤';
                  
                  const activityColor = {
                    'eating': '#4caf50',
                    'drinking': '#2196f3',
                    'waiting': '#ff9800',
                    'seated': '#9e9e9e',
                    'leaving': '#f44336',
                    'preparing_to_eat': '#9c27b0',
                    'using_phone': '#ff5722'
                  }[track.activity] || '#9e9e9e';
                  
                  return (
                    <div 
                      key={track.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: 10,
                        background: '#f9f9f9',
                        borderRadius: 8,
                        border: '1px solid #e0e0e0',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                          background: 'rgba(255,215,0,0.9)',
                          color: '#000',
                          borderRadius: '50%',
                          width: 28,
                          height: 28,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 700,
                          fontSize: 12,
                          flexShrink: 0
                        }}>
                          #{track.id}
                        </div>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>
                            {activityEmoji} User #{track.id}
                          </div>
                          <div style={{ fontSize: 11, opacity: 0.7 }}>
                            {track.activity.replace('_', ' ')} • {track.age}f
                          </div>
                        </div>
                      </div>
                      <div style={{
                        padding: '4px 10px',
                        borderRadius: 12,
                        backgroundColor: activityColor,
                        color: 'white',
                        fontWeight: 600,
                        fontSize: 10,
                        textTransform: 'capitalize',
                        whiteSpace: 'nowrap'
                      }}>
                        {track.activity.replace('_', ' ')}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </Card>
      </ActivitySection>

      {/* Additional Analytics */}
      <Grid>
        <Card>
          <CardTitle>Food Service</CardTitle>
          <div style={{ fontSize: 14, marginTop: 12 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Food Items:</strong> {overlay?.foodSummary?.food_items || 0}
            </div>
            <div>
              <strong>Plates & Cups:</strong> {overlay?.foodSummary?.plates_and_cups || 0}
            </div>
          </div>
        </Card>

        <Card>
          <CardTitle>Detection Model</CardTitle>
          <div style={{ fontSize: 13, marginTop: 12 }}>
            <div style={{ marginBottom: 8 }}>
              <strong>Model:</strong> {overlay?.modelInfo?.model || 'N/A'}
            </div>
            <div>
              <strong>Confidence:</strong> {overlay?.modelInfo?.confidence_threshold || 'N/A'}
            </div>
          </div>
        </Card>

        <Card>
          <CardTitle>Recent Insights</CardTitle>
          <div style={{ fontSize: 13, marginTop: 12 }}>
            {insights.length > 0 ? (
              insights.map((insight, idx) => (
                <div key={idx} style={{ marginBottom: 6, opacity: 0.8 }}>
                  • {insight}
                </div>
              ))
            ) : (
              <div style={{ opacity: 0.6 }}>No insights yet</div>
            )}
          </div>
        </Card>
      </Grid>

      {/* Predictive Analytics Section */}
      <div style={{ marginTop: 40 }}>
        <h2 style={{ color: 'var(--color-primary-800)', marginBottom: 20, fontSize: 22, fontWeight: 700 }}>
          Predictive Intelligence & Performance
        </h2>
        <PredictiveAnalytics />
      </div>

      {/* Camera Modal */}
      <Modal isOpen={cameraModalOpen}>
        <ModalContent style={{ maxWidth: 1200 }}>
          <CloseButton onClick={() => setCameraModalOpen(false)}>×</CloseButton>
          <h2 style={{ marginTop: 0, marginBottom: 24, fontSize: 20, fontWeight: 700 }}>
            Camera Management
          </h2>

          {/* Add camera form */}
          <CameraFormRow>
            <FormField style={{ minWidth: 180 }}>
              <FormLabel>Camera Name</FormLabel>
              <FormInput
                value={newCamName}
                onChange={e => setNewCamName(e.target.value)}
                placeholder="e.g. Main Dining"
                onKeyDown={e => e.key === 'Enter' && handleAddCamera()}
              />
            </FormField>
            <FormField style={{ flex: 2, minWidth: 260 }}>
              <FormLabel>Stream URL (HLS .m3u8, rtsp:// or YouTube)</FormLabel>
              <FormInput
                value={newCamUrl}
                onChange={e => setNewCamUrl(e.target.value)}
                placeholder="https://youtube.com/watch?v=… or https://example.com/stream.m3u8"
                onKeyDown={e => e.key === 'Enter' && handleAddCamera()}
              />
            </FormField>
            <Button
              variant="primary"
              onClick={handleAddCamera}
              disabled={addingCamera || !newCamName.trim() || !newCamUrl.trim()}
              style={{ alignSelf: 'flex-end', minWidth: 120 }}
            >
              {addingCamera ? 'Adding…' : '+ Add Camera'}
            </Button>
          </CameraFormRow>

          {/* Camera list (management) */}
          {cameras.length > 0 && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary-500)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                Saved Cameras
              </div>
              {cameras.map(cam => (
                <CameraListRow key={cam.id}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: 14 }}>
                      <AiStatusBadge $live={aiActiveUrls.has(cam.streamUrl)} />
                      {cam.name}
                    </div>
                    <div style={{ fontSize: 11, color: '#6c757d', marginTop: 2, wordBreak: 'break-all' }}>
                      {cam.streamUrl}
                    </div>
                  </div>
                  <Button
                    variant={aiActiveUrls.has(cam.streamUrl) ? 'success' : 'secondary'}
                    disabled={aiLoadingId === cam.id}
                    onClick={() => handleToggleAI(cam)}
                    style={{ minWidth: 100, fontSize: 12, padding: '6px 12px', minHeight: 32 }}
                  >
                    {aiLoadingId === cam.id
                      ? '…'
                      : aiActiveUrls.has(cam.streamUrl)
                        ? '■ Stop AI'
                        : '▶ Start AI'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleDeleteCamera(cam.id)}
                    style={{ minHeight: 32, padding: '6px 10px', fontSize: 12, color: '#dc2626', borderColor: '#fecaca' }}
                  >
                    Delete
                  </Button>
                </CameraListRow>
              ))}
            </div>
          )}

          {/* Multi-camera live grid */}
          {cameras.length === 0 ? (
            <EmptyCameraHint>
              <div style={{ fontSize: 32, marginBottom: 12 }}>📹</div>
              <strong>No cameras added yet.</strong><br />
              Add a camera above using a YouTube URL, HLS (.m3u8) or RTSP stream URL.<br />
              <span style={{ fontSize: 12, opacity: 0.7 }}>
                e.g. https://youtube.com/watch?v=dQw4w9WgXcQ &nbsp;·&nbsp; https://youtu.be/… &nbsp;·&nbsp; https://example.com/stream.m3u8
              </span>
            </EmptyCameraHint>
          ) : (
            <>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary-500)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
                Live Feeds — click a feed to select it for AI overlay
              </div>
              <CameraGrid $count={cameras.length}>
                {cameras.map(cam => (
                  <CameraCell
                    key={cam.id}
                    $selected={cam.id === (selectedCamera?.id)}
                    onClick={() => setSelectedCameraId(cam.id)}
                  >
                    <CameraLabel>
                      <AiStatusBadge $live={aiActiveUrls.has(cam.streamUrl)} />
                      {cam.name}
                    </CameraLabel>
                    <HLSEmbed url={cam.streamUrl} aiActive={aiActiveUrls.has(cam.streamUrl)} wsRef={wsRef} />
                    <CameraControls onClick={e => e.stopPropagation()}>
                      <SmallButton
                        $success={!aiActiveUrls.has(cam.streamUrl)}
                        $danger={aiActiveUrls.has(cam.streamUrl)}
                        disabled={aiLoadingId === cam.id}
                        onClick={() => handleToggleAI(cam)}
                      >
                        {aiLoadingId === cam.id
                          ? '…'
                          : aiActiveUrls.has(cam.streamUrl)
                            ? '■ Stop AI'
                            : '▶ Start AI'}
                      </SmallButton>
                      <SmallButton $danger onClick={() => handleDeleteCamera(cam.id)}>
                        🗑 Delete
                      </SmallButton>
                    </CameraControls>
                  </CameraCell>
                ))}
              </CameraGrid>

              {/* Selected camera overlay view */}
              {selectedCamera && (
                <div style={{ marginTop: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary-500)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8 }}>
                    AI Overlay — {selectedCamera.name}
                  </div>
                  <VideoContainer ref={playerRef} style={{ position: 'relative' }}>
                    <HLSEmbed url={selectedCamera.streamUrl} aiActive={aiActiveUrls.has(selectedCamera.streamUrl)} wsRef={wsRef} />
                    <CanvasOverlay
                      containerRef={playerRef}
                      overlay={overlay}
                      confirmedTableMap={confirmedTableMap}
                      dbTables={dbTables}
                      confirmedTables={confirmedTables}
                      onTableClick={(tableData) => {
                        setSelectedTable(tableData);
                        setTableManagementOpen(true);
                      }}
                    />
                    <AIHud overlay={overlay} />
                  </VideoContainer>
                </div>
              )}
            </>
          )}
        </ModalContent>
      </Modal>

      {/* Table Management Modal */}
      <TableManagementModal
        isOpen={tableManagementOpen}
        table={selectedTable}
        onClose={() => setTableManagementOpen(false)}
        onSave={async (table) => {
          // Reload confirmed tables immediately to update UI
          try {
            const confirmedResponse = await api.get('/table-management/confirmed?locationId=1');
            setConfirmedTables(confirmedResponse.data.tables || confirmedResponse.data);
            setConfirmedTableMap(confirmedResponse.data.tableMap || {});
            console.log('[TableManagement] Tables reloaded after save:', confirmedResponse.data.tableMap);
            
            // Also reload dashboard tables to update Table Status Overview
            const aiResponse = await api.get('/ai-tables/status?locationId=1');
            const aiTables = aiResponse.data.tables || [];
            const formattedTables = aiTables.map((t: any) => ({
              id: t.id,
              name: t.name,
              status: t.status as 'free'|'occupied'|'needs_clear'|'reserved'
            }));
            setTables(formattedTables);
            console.log('[TableManagement] Dashboard tables updated after save');
          } catch (error) {
            console.error('Failed to reload tables after save:', error);
          }
        }}
      />
    </DashboardContainer>
    </>
  );
}

// ── YouTube helpers ────────────────────────────────────────────────────────────

function isYouTubeUrl(url: string): boolean {
  try {
    const u = new URL(url);
    const h = u.hostname.replace(/^www\./, '');
    return h === 'youtube.com' || h === 'youtu.be';
  } catch {
    return false;
  }
}

function getYouTubeEmbedUrl(url: string): string {
  try {
    const u = new URL(url);
    let videoId = '';
    if (u.hostname === 'youtu.be') {
      videoId = u.pathname.slice(1).split('/')[0] ?? '';
    } else if (u.pathname.startsWith('/live/')) {
      videoId = u.pathname.split('/')[2] ?? '';
    } else if (u.pathname.startsWith('/embed/')) {
      videoId = u.pathname.split('/')[2] ?? '';
    } else {
      videoId = u.searchParams.get('v') ?? '';
    }
    if (!videoId) return url;
    return `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=1&mute=1&controls=1&rel=0`;
  } catch {
    return url;
  }
}

// ── Browser frame capture ─────────────────────────────────────────────────────
// Captures a JPEG frame from a <video> element once per second and sends it
// to the backend via the existing WebSocket as { type: 'ai.frame', payload: { streamUrl, frame } }.
// The backend forwards the frame to Python AI and broadcasts detections back.

// Takes a wsRef (not wsRef.current) so it always reads the live WebSocket value
// even when the connection is established after the component mounts.
function useFrameCapture(
  videoRef: React.RefObject<HTMLVideoElement>,
  streamUrl: string,
  active: boolean,
  wsRef: React.RefObject<WebSocket | null>,
) {
  useEffect(() => {
    if (!active) return;
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    let rafId: number;
    let lastSent = 0;

    const capture = () => {
      rafId = requestAnimationFrame(capture);
      const ws = wsRef.current;
      const video = videoRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN) return;
      if (!video || video.readyState < 2 || !ctx) return;
      const now = Date.now();
      if (now - lastSent < 950) return; // ~1 fps
      lastSent = now;

      // Scale down to 640px wide to keep payload small
      const scale = Math.min(1, 640 / (video.videoWidth || 640));
      canvas.width = Math.round((video.videoWidth || 640) * scale);
      canvas.height = Math.round((video.videoHeight || 480) * scale);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const frame = canvas.toDataURL('image/jpeg', 0.7).split(',')[1];
      if (frame) {
        ws.send(JSON.stringify({ type: 'ai.frame', payload: { streamUrl, frame } }));
      }
    };

    rafId = requestAnimationFrame(capture);
    return () => cancelAnimationFrame(rafId);
  }, [active, wsRef, streamUrl, videoRef]);
}

// ── Supporting Components ─────────────────────────────────────────────────────

const YoutubeEmbed: React.FC<{ url: string; showAiNote?: boolean }> = ({ url, showAiNote }) => {
  const embedUrl = useMemo(() => getYouTubeEmbedUrl(url), [url]);
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', minHeight: 240, background: '#000' }}>
      <iframe
        src={embedUrl}
        style={{ width: '100%', height: '100%', minHeight: 240, border: 'none', display: 'block' }}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        title="YouTube Stream"
      />
      {showAiNote && (
        <div style={{
          position: 'absolute', bottom: 8, left: 8, right: 8,
          background: 'rgba(0,0,0,0.75)', color: '#facc15',
          fontSize: 11, fontWeight: 600, padding: '5px 10px',
          borderRadius: 6, textAlign: 'center', pointerEvents: 'none',
        }}>
          AI overlay unavailable for YouTube — add an HLS/RTSP camera for full AI detection
        </div>
      )}
    </div>
  );
};

const HLSPlayer: React.FC<{ url: string; aiActive?: boolean; wsRef?: React.RefObject<WebSocket | null> }> = ({ url, aiActive, wsRef }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playbackUrl = useMemo(() => streamHlsProxyUrl(url), [url]);
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !playbackUrl) return;
    if (Hls.isSupported()) {
      const hls = new Hls({ maxLiveSyncPlaybackRate: 1.0 });
      hls.loadSource(playbackUrl);
      hls.attachMedia(video);
      const onErr = (_e: any, data: any) => {
        console.error('[HLS] error', data?.type, data?.details);
      };
      hls.on(Hls.Events.ERROR, onErr);
      return () => { hls.off(Hls.Events.ERROR, onErr); hls.destroy(); };
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = playbackUrl;
    }
  }, [playbackUrl]);

  const fallbackRef = useRef<WebSocket | null>(null);
  useFrameCapture(videoRef, url, !!aiActive, wsRef ?? fallbackRef);

  return (
    <video ref={videoRef} style={{ width: '100%', height: '100%', background: '#000' }} autoPlay muted playsInline controls />
  );
};

const HLSEmbed: React.FC<{ url: string; aiActive?: boolean; wsRef?: React.RefObject<WebSocket | null> }> = ({ url, aiActive, wsRef }) => {
  if (isYouTubeUrl(url)) {
    return <YoutubeEmbed url={url} showAiNote={aiActive} />;
  }
  return <HLSPlayer url={url} aiActive={aiActive} wsRef={wsRef} />;
};

function AIHud({ overlay }: { overlay: OverlayData | null }) {
  if (!overlay) return null;
  return (
    <div style={{
      position: 'absolute',
      top: 80,
      right: 16,
      background: 'rgba(0,0,0,0.9)',
      color: 'white',
      padding: 16,
      borderRadius: 12,
      minWidth: 200
    }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>🤖 AI Detection</div>
      <div style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
        👥 {overlay.peopleCount} People
      </div>
      <div style={{ fontSize: 12 }}>
        Tracking: {overlay.trackedObjects?.length || 0} individuals
      </div>
    </div>
  );
}

function CanvasOverlay({ containerRef, overlay, confirmedTableMap, confirmedTables, dbTables, onTableClick }: { 
  containerRef: React.RefObject<HTMLDivElement>; 
  overlay: OverlayData | null;
  confirmedTableMap?: Record<number, any>;
  confirmedTables?: any[];
  dbTables?: Array<{ id:number; name:string; status:string; capacity:number }>;
  onTableClick?: (tableData: any) => void;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tableAreas, setTableAreas] = useState<any[]>([]);
  
  // Utility: IOU match between two bboxes [x1,y1,x2,y2]
  const calculateIOU = (a: number[], b: number[]) => {
    const xA = Math.max(a[0], b[0]);
    const yA = Math.max(a[1], b[1]);
    const xB = Math.min(a[2], b[2]);
    const yB = Math.min(a[3], b[3]);
    const interW = Math.max(0, xB - xA);
    const interH = Math.max(0, yB - yA);
    const inter = interW * interH;
    const areaA = Math.max(0, (a[2]-a[0])) * Math.max(0, (a[3]-a[1]));
    const areaB = Math.max(0, (b[2]-b[0])) * Math.max(0, (b[3]-b[1]));
    const union = areaA + areaB - inter;
    if (union <= 0) return 0;
    return inter / union;
  };
  useEffect(() => {
    const el = containerRef.current;
    const cvs = canvasRef.current;
    if (!el || !cvs || !overlay) return;
    
    const rect = el.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;
    cvs.width = w;
    cvs.height = h;
    
    const ctx = cvs.getContext('2d');
    if (!ctx) return;
    
    ctx.clearRect(0, 0, w, h);
    
    const sx = w / overlay.width;
    const sy = h / overlay.height;
    
    // Draw detections
    overlay.detections.forEach(detection => {
      const [x1, y1, x2, y2] = detection.bbox;
      const scaledX1 = x1 * sx;
      const scaledY1 = y1 * sy;
      const scaledX2 = x2 * sx;
      const scaledY2 = y2 * sy;
      
      let color = '#2196f3';
      if (detection.class === 'person') color = '#f44336';
      if (detection.class === 'dining table') color = '#4caf50';
      
      ctx.strokeStyle = color;
      ctx.lineWidth = 3;
      ctx.strokeRect(scaledX1, scaledY1, scaledX2 - scaledX1, scaledY2 - scaledY1);
    });
    
    // Draw tracking IDs
    overlay.trackedObjects?.forEach(track => {
      const [x1, y1, x2, y2] = track.bbox;
      const centerX = ((x1 + x2) / 2) * sx;
      const centerY = ((y1 + y2) / 2) * sy;
      
      ctx.fillStyle = 'rgba(255,215,0,0.9)';
      ctx.beginPath();
      ctx.arc(centerX, centerY, 20, 0, 2 * Math.PI);
      ctx.fill();
      
      ctx.fillStyle = '#000';
      ctx.font = 'bold 14px sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(`#${track.id}`, centerX, centerY);
    });
    
    // Draw table numbers on detected tables
    if (overlay.tableStates && Object.keys(overlay.tableStates).length > 0) {
      // Convert table data to array and sort by position (left to right, top to bottom)
      const tableEntries = Object.entries(overlay.tableStates)
        .filter(([_, tableData]) => tableData && tableData.bbox)
        .map(([tableName, tableData]) => {
          const centerXRaw = (tableData.bbox[0] + tableData.bbox[2]) / 2;
          const centerYRaw = (tableData.bbox[1] + tableData.bbox[3]) / 2;
          return {
            name: tableName,
            data: tableData,
            centerX: centerXRaw,
            centerY: centerYRaw
          };
        })
        .sort((a, b) => {
          // Sort by Y position first (top to bottom), then X position (left to right)
          if (Math.abs(a.centerY - b.centerY) < 50) {
            return a.centerX - b.centerX; // Same row, sort left to right
          }
          return a.centerY - b.centerY; // Different rows, sort top to bottom
        });

      // Store table click areas for interaction
      const clickableAreas: any[] = [];

      tableEntries.forEach((tableEntry, index) => {
        const { name: tableName, data: tableData } = tableEntry;
        const [x1, y1, x2, y2] = tableData.bbox;
        const scaledX1 = x1 * sx;
        const scaledY1 = y1 * sy;
        const scaledX2 = x2 * sx;
        const scaledY2 = y2 * sy;

        // Calculate table center
        const centerX = (scaledX1 + scaledX2) / 2;
        const centerY = (scaledY1 + scaledY2) / 2;

        // Extract table number from name (format: "table_X" where X is the table ID)
        // All tables are now persistent with stable IDs
        let tableNum;
        if (tableName.startsWith('table_')) {
          tableNum = parseInt(tableName.split('_')[1] || '1');
        } else {
          tableNum = index + 1; // Fallback for unexpected format
        }
        
        // Match user-overridden table by IOU with confirmed tables' stored bbox (if available)
        let matchedConfirmed: any = null;
        if (confirmedTables && confirmedTables.length > 0) {
          for (const ct of confirmedTables) {
            if (ct.boundingBox) {
              const ctBox = [ct.boundingBox.x1, ct.boundingBox.y1, ct.boundingBox.x2, ct.boundingBox.y2];
              const iou = calculateIOU([x1, y1, x2, y2], ctBox);
              if (iou >= 0.25) { // Moderate IOU threshold
                matchedConfirmed = ct;
                break;
              }
            }
          }
        }

        // Fallback to table number map if no IOU match
        const confirmedTable = matchedConfirmed || confirmedTableMap?.[tableNum];
        // If DB has a table with same name (Table X or custom) prefer its status/name
        let dbMatchName = '';
        if (dbTables && dbTables.length > 0) {
          const fallbackName = `Table ${tableNum}`;
          const preferredName = confirmedTable?.displayName || fallbackName;
          const found = dbTables.find(t => t.name === preferredName || t.name === fallbackName);
          if (found) {
            dbMatchName = found.name;
          }
        }

        const displayName = dbMatchName || confirmedTable?.displayName || `TABLE ${tableNum}`;
        const isConfirmed = !!confirmedTable || !!dbMatchName;
        
        // Choose color based on state
        let bgColor = 'rgba(76,175,80,0.9)'; // green for occupied
        if (tableData.state === 'needs_clear') {
          bgColor = 'rgba(255,152,0,0.9)'; // orange
        } else if (tableData.state === 'clean') {
          bgColor = 'rgba(158,158,158,0.9)'; // gray
        } else if (tableData.state === 'waiting_for_food') {
          bgColor = 'rgba(33,150,243,0.9)'; // blue
        }
        
        // Draw large table number badge
        ctx.font = 'bold 20px sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const textMetrics = ctx.measureText(displayName);
        const textWidth = textMetrics.width;
        const badgeWidth = textWidth + 24;
        const badgeHeight = 36;
        
        // Draw background badge
        ctx.fillStyle = bgColor;
        ctx.beginPath();
        ctx.roundRect(centerX - badgeWidth/2, centerY - badgeHeight/2, badgeWidth, badgeHeight, 8);
        ctx.fill();
        
        // Draw white border
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 2;
        ctx.stroke();
        
        // Draw text
        ctx.fillStyle = 'white';
        ctx.fillText(displayName, centerX, centerY);
        
        // Draw checkmark for confirmed tables
        if (isConfirmed) {
          ctx.font = 'bold 16px sans-serif';
          ctx.fillStyle = '#4caf50';
          ctx.fillText('✓', centerX + badgeWidth/2 - 10, centerY - badgeHeight/2 - 5);
        }
        
        // Draw state label below if needed (skip for temporary tables to avoid clutter)
        if (tableData.state === 'needs_clear' && !tableData.temporary) {
          ctx.font = 'bold 12px sans-serif';
          const stateLabel = '⚠️ NEEDS CLEAR';
          const stateLabelWidth = ctx.measureText(stateLabel).width;

          ctx.fillStyle = 'rgba(244,67,54,0.95)';
          ctx.fillRect(centerX - stateLabelWidth/2 - 6, centerY + 26, stateLabelWidth + 12, 22);

          ctx.fillStyle = 'white';
          ctx.fillText(stateLabel, centerX, centerY + 37);
        }

        // Reset text alignment
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';

        // Store clickable area for this table
        clickableAreas.push({
          x: scaledX1,
          y: scaledY1,
          width: scaledX2 - scaledX1,
          height: scaledY2 - scaledY1,
          tableData: {
            id: confirmedTable?.id,
            tableNumber: tableNum,
            displayName,
            capacity: confirmedTable?.capacity || 4,
            boundingBox: { x1, y1, x2, y2 },
            position: { x: (x1 + x2) / 2, y: (y1 + y2) / 2 },
            confirmed: isConfirmed
          }
        });
      });

      // Update state with clickable areas
      setTableAreas(clickableAreas);
    }
  }, [containerRef, overlay, confirmedTableMap, confirmedTables, dbTables]);

  // Add click handler for tables
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !onTableClick) return;

    const handleClick = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Check if click is within any table area
      for (const area of tableAreas) {
        if (x >= area.x && x <= area.x + area.width &&
            y >= area.y && y <= area.y + area.height) {
          onTableClick(area.tableData);
          break;
        }
      }
    };

    canvas.addEventListener('click', handleClick);
    return () => canvas.removeEventListener('click', handleClick);
  }, [tableAreas, onTableClick]);
  
  return <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: onTableClick ? 'auto' : 'none', cursor: onTableClick ? 'pointer' : 'default' }} />;
}

